import type { StatementLine } from '../schema/reconciliation.js'

/**
 * Matching a paper statement to captured transactions — REQUIREMENTS.md
 * §6.6, §7.2.
 *
 * Every example here is real, taken from the June 2026 PNC statement and the
 * packet submitted against it. The two are a matched pair: the statement's 16
 * lines, in post-date order, split 10 and 6, reproduce that packet's page
 * subtotals of $1,316.75 and $522.99 exactly.
 *
 * The problem this module exists for: a statement does not name merchants the
 * way a receipt does. `SQ *SUBPLICITY BREEZEWOOD PA` is the same purchase as a
 * receipt reading "Subplicity". §8.1 requires the log sheet to print the
 * *statement's* spelling — that retyping is what costs Matt hours per cycle —
 * so both spellings have to be kept, and the match between them cannot be a
 * string comparison.
 */

/**
 * What matching needs from a statement row — a subset of the stored
 * `statementLine` rather than a second definition of one.
 *
 * `transactionDate`, not `postedDate`. The statement is *sorted* by posting
 * date, which is why it drives packet item order, but the purchase date is
 * what a receipt agrees with. They differ by a day or three, so matching on
 * the posting date silently misses.
 *
 * `merchantAsPrinted` is verbatim: §8.1 puts the statement's own spelling on
 * the log sheet, so it is never overwritten with a tidied version.
 */
export type MatchableStatementLine = Pick<
  StatementLine,
  'lineIndex' | 'transactionDate' | 'merchantAsPrinted' | 'amount'
>

/**
 * Payment-processor prefixes. These identify who processed the charge, not who
 * was paid: `SQ *` is Square, `TST*` is Toast. Every one below appears on the
 * reference statement.
 */
const PROCESSOR_PREFIXES = [
  /^sq\s*\*\s*/i,
  /^tst\s*\*\s*/i,
  /^sp\s+/i,
  /^py\s*\*\s*/i,
  /^pp\s*\*\s*/i,
  /^paypal\s*\*\s*/i,
  /^wpy\s*\*\s*/i,
  /^ci\s*\*\s*/i,
]

/** US state codes, for stripping the trailing location. */
const STATE_CODES = new Set([
  'al','ak','az','ar','ca','co','ct','de','fl','ga','hi','id','il','in','ia',
  'ks','ky','la','me','md','ma','mi','mn','ms','mo','mt','ne','nv','nh','nj',
  'nm','ny','nc','nd','oh','ok','or','pa','ri','sc','sd','tn','tx','ut','vt',
  'va','wa','wv','wi','wy','dc',
])

/**
 * Reduce a statement descriptor to something matchable.
 *
 * Deliberately lossy, and only ever used for *comparison* — the verbatim
 * description is what the log sheet prints. Worked through on the real line
 * `SQ *SUBPLICITY BREEZEWOOD PA`: drop the processor prefix, drop the trailing
 * state, drop the city, and "SUBPLICITY" is left to match a receipt.
 */
export function normalizeStatementVendor(description: string): string {
  let text = description.trim()

  for (const prefix of PROCESSOR_PREFIXES) {
    text = text.replace(prefix, '')
  }

  // Long digit runs are transaction and merchant IDs, never part of a name:
  // `EXPEDIA 72074954856144 EXPEDIA.COM WA`, `SUNOCO 0076699800 BEDFORD PA`.
  // Short numbers are kept — `WINGSTOP AC118` and `GOMOBILEPGH 2` need them
  // no more than `7-ELEVEN` can lose its.
  text = text.replace(/\b\d{5,}\b/g, ' ')
  // Phone numbers and web addresses are contact details, not the merchant.
  text = text.replace(/\b\d{3}-\d{3}-\d{4}\b/g, ' ')
  text = text.replace(/\b(?:www\.|https?:\/\/)\S+/gi, ' ')
  text = text.replace(/\S+@\S+/g, ' ')

  const words = text.split(/\s+/).filter((w) => w !== '')

  // A trailing state code, then whatever city preceded it. Only one city word
  // is dropped: `BELL VERNON PA` loses "VERNON" but keeps "BELL", which is
  // wrong in principle and harmless in practice — matching is by shared word,
  // and "WINGSTOP" still carries it.
  if (words.length > 1 && STATE_CODES.has((words.at(-1) ?? '').toLowerCase())) {
    words.pop()
    if (words.length > 1) words.pop()
  }

  return words.join(' ').replace(/[*,]/g, '').trim()
}

/** Distinctive words of a name, for comparison. */
function tokens(name: string): Set<string> {
  return new Set(
    name
      .toLowerCase()
      .split(/[^a-z0-9]+/)
      .filter((t) => t.length >= 3),
  )
}

/**
 * Could these two names be the same merchant?
 *
 * One shared distinctive word is enough, because that is genuinely all the
 * signal there is: `CONWELL INN PHILADELPHIA PA` against "Conwell Inn",
 * `SOUTHWES …` against "Southwest Airlines". It is weak on its own and is
 * never used on its own — the caller also requires the exact amount.
 */
export function vendorsCouldMatch(statementVendor: string, receiptVendor: string): boolean {
  const left = tokens(normalizeStatementVendor(statementVendor))
  const right = tokens(receiptVendor)
  if (left.size === 0 || right.size === 0) return false

  for (const word of right) {
    if (left.has(word)) return true
    // `SOUTHWES` is the statement's truncation of "Southwest". Prefix
    // containment catches the truncation without matching unrelated words.
    for (const candidate of left) {
      if (candidate.length >= 5 && word.length >= 5) {
        if (candidate.startsWith(word) || word.startsWith(candidate)) return true
      }
    }
  }
  return false
}

/** How a statement line ended up. */
export type StatementMatchKind = 'matched' | 'amount_only' | 'unmatched'

export interface MatchableTransaction {
  id: string
  vendor: string
  amount: number
  transactionDate: string
}

export interface StatementMatch {
  line: MatchableStatementLine
  kind: StatementMatchKind
  transactionId?: string
  reason: string
}

/** §7.2: a purchase and its posting are days apart, not weeks. */
const MATCH_WINDOW_DAYS = 10

/**
 * Match each statement line to a captured transaction.
 *
 * Two passes, and the order matters. Amount *and* vendor first, across every
 * line, so a confident match claims its transaction before a weaker one can;
 * then amount alone for whatever is left. Doing it line by line would let the
 * first of two identical amounts take the transaction the second one actually
 * belonged to.
 *
 * A transaction is claimed once. Two $18.77 Sunoco fill-ups on one statement
 * are two fill-ups, and collapsing them into one would drop a charge from the
 * packet — which is the failure that gets noticed by the business office
 * rather than by us.
 */
export function matchStatementLines(
  lines: readonly MatchableStatementLine[],
  transactions: readonly MatchableTransaction[],
): StatementMatch[] {
  const claimed = new Set<string>()
  const results = new Map<number, StatementMatch>()

  const candidatesFor = (line: MatchableStatementLine, requireVendor: boolean) =>
    transactions
      .filter((t) => !claimed.has(t.id))
      .filter((t) => Math.round(t.amount * 100) === Math.round(line.amount * 100))
      .filter((t) => withinDays(t.transactionDate, line.transactionDate, MATCH_WINDOW_DAYS))
      .filter((t) => !requireVendor || vendorsCouldMatch(line.merchantAsPrinted, t.vendor))
      .sort(
        (a, b) =>
          dayGap(a.transactionDate, line.transactionDate) -
          dayGap(b.transactionDate, line.transactionDate),
      )

  for (const line of lines) {
    const best = candidatesFor(line, true)[0]
    if (best !== undefined) {
      claimed.add(best.id)
      results.set(line.lineIndex, {
        line,
        kind: 'matched',
        transactionId: best.id,
        reason: `Matches ${best.vendor} — same amount, same vendor, within ${MATCH_WINDOW_DAYS} days.`,
      })
    }
  }

  for (const line of lines) {
    if (results.has(line.lineIndex)) continue

    const best = candidatesFor(line, false)[0]
    if (best !== undefined) {
      claimed.add(best.id)
      results.set(line.lineIndex, {
        line,
        kind: 'amount_only',
        transactionId: best.id,
        reason: `Same amount as ${best.vendor}, but the names don't obviously agree — worth a look.`,
      })
      continue
    }

    results.set(line.lineIndex, {
      line,
      kind: 'unmatched',
      reason: 'Nothing captured for this charge. It still appears on the packet.',
    })
  }

  // Statement order is packet order, so results come back in line order.
  return lines.map((line) => results.get(line.lineIndex)!)
}

const MS_PER_DAY = 86_400_000

function dayGap(a: string, b: string): number {
  return Math.abs(new Date(a).getTime() - new Date(b).getTime()) / MS_PER_DAY
}

function withinDays(a: string, b: string, days: number): boolean {
  const gap = dayGap(a, b)
  return Number.isFinite(gap) && gap <= days
}

/** Totals for the confirmation screen (§7.2). */
export function summarizeMatches(matches: readonly StatementMatch[]) {
  const count = (kind: StatementMatchKind) => matches.filter((m) => m.kind === kind).length
  return {
    total: matches.length,
    matched: count('matched'),
    amountOnly: count('amount_only'),
    unmatched: count('unmatched'),
  }
}
